/**
 * Trial abuse prevention configuration.
 * 
 * Feature flags and settings for trial restriction layers.
 */

export const TRIAL_CONFIG = {
  /** Device fingerprint tracking — active immediately */
  deviceTrackingEnabled: true,

  /** Phone verification gate — disabled until Supabase Auth phone provider is configured */
  phoneVerificationEnabled: false,

  /** Full trial duration for new devices (days) */
  fullTrialDays: 7,

  /** Reduced trial for repeat devices (days) */
  repeatTrialDays: 1,

  /** Post-trial restrictions for basic tier (applies after trial expires) */
  postTrialRestrictions: {
    /** Max free tool uses per day after trial expires */
    maxDailyToolUses: 3,
    /** AI preview actions allowed after trial */
    aiPreviewsAllowed: 0,
    /** Advanced outputs (PDFs, reports) blocked */
    advancedOutputsBlocked: true,
    /** Show upgrade prompts aggressively */
    aggressiveUpgradePrompts: true,
  },

  /** 
   * Repeat-device restrictions — applied DURING the reduced 1-day trial.
   * Stricter than normal trial, weaker than post-trial.
   */
  repeatDeviceRestrictions: {
    /** AI previews completely disabled for repeat devices */
    aiPreviewsDisabled: true,
    /** Max tool uses per day during repeat trial */
    maxDailyToolUses: 2,
    /** Advanced outputs blocked */
    advancedOutputsBlocked: true,
    /** Advisor access disabled */
    advisorAccessDisabled: true,
    /** Show monetization push aggressively */
    aggressiveMonetization: true,
  },
} as const;

/** Check if phone verification should be shown */
export function isPhoneVerificationAvailable(): boolean {
  return TRIAL_CONFIG.phoneVerificationEnabled;
}

/** Check if device tracking is active */
export function isDeviceTrackingActive(): boolean {
  return TRIAL_CONFIG.deviceTrackingEnabled;
}

/**
 * Get the active restriction set based on trial state.
 * Returns the strictest applicable restrictions.
 */
export function getActiveRestrictions(opts: {
  isRepeatDevice: boolean;
  trialExpired: boolean;
  userTier: string;
}) {
  // Non-basic users → no restrictions
  if (opts.userTier !== "basic") {
    return null;
  }

  // Trial expired → post-trial restrictions (strictest)
  if (opts.trialExpired) {
    return {
      ...TRIAL_CONFIG.postTrialRestrictions,
      aiPreviewsDisabled: true,
      advisorAccessDisabled: true,
      label: "trial_expired" as const,
    };
  }

  // Repeat device during active trial → repeat restrictions
  if (opts.isRepeatDevice) {
    return {
      ...TRIAL_CONFIG.repeatDeviceRestrictions,
      aiPreviewsAllowed: 0,
      label: "repeat_device" as const,
    };
  }

  // Normal trial user → no restrictions
  return null;
}
